import React, { useState, useRef } from 'react';
import { ChevronDown, Check, Loader2 } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { SUPPORTED_LANGUAGES, type LanguageCode } from '@/lib/i18n/translate';
import { useClickOutside } from '@/hooks/useClickOutside';

interface LanguageSelectorProps {
  variant?: 'light' | 'dark';
}

/**
 * Compact language selector for app headers.
 * Shows flag + chevron; dropdown opens downward.
 * Persists via TranslationProvider.setLanguage (writes to profiles.preferred_language when authed).
 *
 * variant="dark"  — admin sidebar / dark surfaces (default)
 * variant="light" — parent portal / pre-auth pages
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ variant = 'dark' }) => {
  const { language, setLanguage } = useTranslation();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useClickOutside(ref, () => setOpen(false));

  const current = SUPPORTED_LANGUAGES.find(l => l.code === language);
  const isLight = variant === 'light';

  const handleSelect = async (code: LanguageCode) => {
    if (code === language) {
      setOpen(false);
      return;
    }
    setSaving(true);
    setOpen(false);
    await setLanguage(code);
    setSaving(false);
  };

  const triggerClass = isLight
    ? 'flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[#636366] hover:text-[#1C1C1E] hover:bg-black/[0.05] transition-all min-w-[44px] min-h-[44px] justify-center'
    : 'flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/8 transition-all min-w-[44px] min-h-[44px] justify-center';

  const dropdownClass = isLight
    ? 'absolute right-0 top-full mt-2 w-48 max-h-72 overflow-y-auto rounded-xl glass-elevated border border-black/[0.08] shadow-lg z-50'
    : 'absolute right-0 top-full mt-2 w-48 max-h-72 overflow-y-auto rounded-xl border border-white/10 shadow-xl z-50';

  const dropdownStyle: React.CSSProperties = isLight
    ? { position: 'absolute' }
    : { position: 'absolute', background: 'rgba(30,30,30,0.95)', backdropFilter: 'blur(20px)' };

  const accent = isLight ? '#5C2D91' : '#0A84FF';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={triggerClass}
        title={current?.nativeName || 'Language'}
      >
        {saving ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <span className="text-base leading-none">{current?.flag || '🌐'}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {open && (
        <div className={dropdownClass} style={dropdownStyle}>
          {SUPPORTED_LANGUAGES.map(lang => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`w-full px-3 py-2 flex items-center justify-between text-left transition-colors ${
                language === lang.code
                  ? `bg-[${accent}]/20 text-[${accent}]`
                  : isLight ? 'text-[#1C1C1E] hover:bg-[#5C2D91]/10' : 'text-white/80 hover:bg-[#0A84FF]/20'
              }`}
              style={language === lang.code ? { backgroundColor: `${accent}20`, color: accent } : undefined}
            >
              <div className="flex items-center gap-2">
                <span className="text-base">{lang.flag}</span>
                <span className="text-sm">{lang.nativeName}</span>
              </div>
              {language === lang.code && <Check className="w-4 h-4" style={{ color: accent }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
